import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, addDoc, serverTimestamp, deleteDoc, setDoc } from '../firebase.config';
import { useLanguage } from '../contexts/LanguageContext';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { getCollectionForCompany } from '../utils/dataAdapter';
import { useCompanyCurrency } from '../hooks/useCompanyCurrency';
import { formatCurrency, getCurrencySymbol } from '../utils/currencyUtils';
import { formatDateToCompanyFormat } from '../utils/dateUtils';
import { useTablePagination } from '../utils/tablePagination';
import PaginationControls from '../utils/PaginationControls';
import { useNotification } from '../contexts/NotificationContext';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';

// Investment Card Component
const InvestmentCard = ({ investment, isExpanded, onToggle, onEdit, onDelete, companyCurrency }) => {
  const currentValue = investment.currentValue || investment.amount || 0;
  const isMatured = investment.maturityDate && new Date(investment.maturityDate) < new Date();
  const daysToMaturity = investment.maturityDate 
    ? Math.ceil((new Date(investment.maturityDate) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
        onClick={() => onToggle(investment.id)}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-800 truncate">
                {investment.investmentType || 'Investment'}
              </h3>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                isMatured ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
              }`}>
                {isMatured ? 'Matured' : 'Active'}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {investment.maturityDate ? formatDateToCompanyFormat(investment.maturityDate, {}) : 'No maturity date'} • 
              {investment.interestRate ? ` ${investment.interestRate}% p.a.` : ''}
              {daysToMaturity !== null && !isMatured && (
                <span className="ml-2">• {daysToMaturity} days to maturity</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xl font-bold text-green-600">
                {formatCurrency(currentValue, companyCurrency)}
              </p>
              <p className="text-xs text-gray-500">Current Value</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(investment);
                }}
                className="p-2 text-blue-600 hover:bg-blue-100 rounded-full transition-colors"
                title="Edit"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(investment.id);
                }}
                className="p-2 text-red-600 hover:bg-red-100 rounded-full transition-colors"
                title="Delete"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
              <button
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title={isExpanded ? 'Collapse' : 'Expand'}
              >
                <svg 
                  className={`w-4 h-4 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Investment Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Type:</span>
                  <span className="font-medium">{investment.investmentType || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Investment Date:</span>
                  <span className="font-medium">{formatDateToCompanyFormat(investment.investmentDate, {})}</span>
                </div>
                {investment.maturityDate && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Maturity Date:</span>
                    <span className="font-medium">{formatDateToCompanyFormat(investment.maturityDate, {})}</span>
                  </div>
                )}
                {investment.institution && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Institution:</span>
                    <span className="font-medium">{investment.institution}</span>
                  </div>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Financial Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Principal Amount:</span>
                  <span className="font-medium">{formatCurrency(investment.amount || 0, companyCurrency)}</span>
                </div>
                {investment.interestRate && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Interest Rate:</span>
                    <span className="font-medium">{investment.interestRate}% per annum</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Current Value:</span>
                  <span className="font-bold text-green-600 text-lg">{formatCurrency(currentValue, companyCurrency)}</span>
                </div>
                {investment.amount && currentValue > investment.amount && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Returns:</span>
                    <span className="font-medium text-green-600">
                      +{formatCurrency(currentValue - investment.amount, companyCurrency)}
                    </span>
                  </div>
                )}
                {investment.description && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Description:</span>
                    <span className="font-medium">{investment.description}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function Investments({ db, userId, isAuthReady, appId }) {
  const { currentCompany } = useEnhancedCompany();
  const companyId = currentCompany?.companyId || currentCompany?.id;
  const { currency: companyCurrency } = useCompanyCurrency();
  const { t } = useLanguage();
  const { success, error, warning } = useNotification();

  const [investments, setInvestments] = useState([]);
  const [expandedInvestments, setExpandedInvestments] = useState({});
  const [editingInvestmentId, setEditingInvestmentId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingInvestmentId, setDeletingInvestmentId] = useState(null);

  // Form state
  const [investmentType, setInvestmentType] = useState('FD');
  const [investmentDate, setInvestmentDate] = useState(new Date().toISOString().split('T')[0]);
  const [amount, setAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [maturityDate, setMaturityDate] = useState('');
  const [institution, setInstitution] = useState('');
  const [description, setDescription] = useState('');

  // Filter states
  const [filterSearch, setFilterSearch] = useState('');
  const [filterType, setFilterType] = useState('');

  const investmentTypes = ['FD', 'RD', 'Mutual Fund', 'Stocks', 'Bonds', 'PPF', 'NSC', 'Other'];

  // Calculate current value based on interest
  const calculateCurrentValue = (principal, investmentDate, maturityDate, interestRate) => {
    if (!principal || !investmentDate) return principal || 0;
    if (!interestRate) return principal;
    
    const start = new Date(investmentDate);
    const end = maturityDate ? new Date(maturityDate) : new Date();
    const now = new Date();
    const endDate = end < now ? end : now;
    
    const years = (endDate - start) / (1000 * 60 * 60 * 24 * 365);
    const interest = principal * (interestRate / 100) * years;
    return principal + interest;
  };

  // Fetch investments
  useEffect(() => {
    if (db && userId && isAuthReady && companyId) {
      const investmentsCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'investments');
      if (!investmentsCollectionRef) {
        setInvestments([]);
        return;
      }
      
      const unsubscribe = onSnapshot(investmentsCollectionRef, (snapshot) => {
        const arr = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          const currentValue = calculateCurrentValue(
            data.amount,
            data.investmentDate,
            data.maturityDate,
            data.interestRate
          );
          arr.push({ 
            id: doc.id, 
            ...data,
            currentValue 
          });
        });
        setInvestments(arr);
      }, (err) => {
        console.error('Error fetching investments:', err);
        setInvestments([]);
      });
      return () => {
        unsubscribe();
        setInvestments([]);
      };
    } else {
      setInvestments([]);
    }
  }, [db, userId, isAuthReady, appId, companyId]);

  // Toggle expand
  const toggleExpand = (investmentId) => {
    setExpandedInvestments(prev => ({
      ...prev,
      [investmentId]: !prev[investmentId]
    }));
  };

  // Handle edit
  const handleEdit = (investment) => {
    setEditingInvestmentId(investment.id);
    setInvestmentType(investment.investmentType || 'FD');
    setInvestmentDate(investment.investmentDate || new Date().toISOString().split('T')[0]);
    setAmount(investment.amount?.toString() || '');
    setInterestRate(investment.interestRate?.toString() || '');
    setMaturityDate(investment.maturityDate || '');
    setInstitution(investment.institution || '');
    setDescription(investment.description || '');
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = (investmentId) => {
    setDeletingInvestmentId(investmentId);
    setShowDeleteConfirm(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!db || !userId || !appId || !deletingInvestmentId) return;
    try {
      const investmentRef = doc(getCollectionForCompany(db, { companyId, appId, userId }, 'investments'), deletingInvestmentId);
      await deleteDoc(investmentRef);
      success('Investment deleted successfully');
      setShowDeleteConfirm(false);
      setDeletingInvestmentId(null);
    } catch (err) {
      error('Error deleting investment: ' + err.message);
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!db || !userId || !appId) return;
    if (!amount || parseFloat(amount) <= 0) {
      warning('Please enter valid investment amount');
      return;
    }

    try {
      const investmentsCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'investments');
      const currentValue = calculateCurrentValue(
        parseFloat(amount),
        investmentDate,
        maturityDate,
        parseFloat(interestRate) || 0
      );
      const investmentData = {
        investmentType,
        investmentDate,
        amount: parseFloat(amount),
        interestRate: interestRate ? parseFloat(interestRate) : 0,
        maturityDate: maturityDate || null,
        institution: institution.trim(),
        currentValue,
        description: description.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (editingInvestmentId) {
        const investmentRef = doc(investmentsCollectionRef, editingInvestmentId);
        await setDoc(investmentRef, investmentData, { merge: true });
        success('Investment updated successfully');
      } else {
        await addDoc(investmentsCollectionRef, investmentData);
        success('Investment added successfully');
      }

      // Reset form
      setInvestmentType('FD');
      setInvestmentDate(new Date().toISOString().split('T')[0]);
      setAmount('');
      setInterestRate('');
      setMaturityDate('');
      setInstitution('');
      setDescription('');
      setEditingInvestmentId(null);
      setShowForm(false);
    } catch (err) {
      error('Error saving investment: ' + err.message);
    }
  };

  // Filter investments
  const filteredInvestments = (investments || []).filter(investment => {
    const matchesSearch = !filterSearch || 
      (investment.investmentType || '').toLowerCase().includes(filterSearch.toLowerCase()) ||
      (investment.institution || '').toLowerCase().includes(filterSearch.toLowerCase());
    const matchesType = !filterType || investment.investmentType === filterType;
    return matchesSearch && matchesType;
  });

  // Table pagination
  const pagination = useTablePagination(filteredInvestments || [], 10);

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Investments</h1>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingInvestmentId(null);
            setInvestmentType('FD');
            setInvestmentDate(new Date().toISOString().split('T')[0]);
            setAmount('');
            setInterestRate('');
            setMaturityDate('');
            setInstitution('');
            setDescription('');
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md"
        >
          + Add Investment
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4 bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="Search investments..."
            />
          </div>
          <div className="sm:w-48">
            <AppSelect
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              aria-label="Filter by type"
              sheetTitle="Investment type"
              options={[
                { value: '', label: 'All Types' },
                ...investmentTypes.map((type) => ({ value: type, label: type })),
              ]}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER} text-sm`}
            />
          </div>
        </div>
      </div>

      {/* Investments List */}
      <div className="space-y-4">
        {(pagination.currentData || []).map(investment => (
          <InvestmentCard
            key={investment.id}
            investment={investment}
            isExpanded={expandedInvestments[investment.id]}
            onToggle={toggleExpand}
            onEdit={handleEdit}
            onDelete={handleDelete}
            companyCurrency={companyCurrency}
          />
        ))}
        {(!pagination.currentData || pagination.currentData.length === 0) && (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg">No investments found</p>
            <p className="text-sm mt-2">Add your first investment to get started</p>
          </div>
        )}
      </div>

      <PaginationControls {...pagination} />

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[92dvh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-4">
                {editingInvestmentId ? 'Edit Investment' : 'Add Investment'}
              </h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Investment Type *
                    </label>
                    <AppSelect
                      value={investmentType}
                      onChange={(e) => setInvestmentType(e.target.value)}
                      aria-label="Investment Type"
                      sheetTitle="Investment Type"
                      options={investmentTypes.map((type) => ({ value: type, label: type }))}
                      triggerClassName={APP_SELECT_FILTER_TRIGGER}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Investment Date *
                    </label>
                    <input
                      type="date"
                      value={investmentDate}
                      onChange={(e) => setInvestmentDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount *
                    </label>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Interest Rate (% p.a.)
                    </label>
                    <input
                      type="number"
                      value={interestRate}
                      onChange={(e) => setInterestRate(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="0.00"
                      min="0"
                      step="0.1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Maturity Date
                    </label>
                    <input
                      type="date"
                      value={maturityDate}
                      onChange={(e) => setMaturityDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Institution/Bank
                    </label>
                    <input
                      type="text"
                      value={institution}
                      onChange={(e) => setInstitution(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="e.g., SBI, HDFC Bank"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    rows="3"
                    placeholder="Additional details about the investment"
                  />
                </div>
                {amount && investmentDate && interestRate && (
                  <div className="bg-blue-50 p-4 rounded-md">
                    <p className="text-sm text-gray-700">
                      <strong>Current Value:</strong> {formatCurrency(
                        calculateCurrentValue(
                          parseFloat(amount),
                          investmentDate,
                          maturityDate,
                          parseFloat(interestRate)
                        ),
                        companyCurrency
                      )}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleSave}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md"
                >
                  {editingInvestmentId ? 'Update' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditingInvestmentId(null);
                  }}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6 max-h-[92dvh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">Confirm Delete</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this investment? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md"
              >
                Delete
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingInvestmentId(null);
                }}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-md"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Investments;

